import { isNil, uniq } from 'lodash';
import { Field } from '@shared/data/entity/fields/Field';
import { FieldRecord } from '../types';

export function isFieldRecordModified(originalFields: FieldRecord, updatedFields: FieldRecord) {
  // If the number of fields is different, they are dirty
  const originalFieldKeys = Object.keys(originalFields);
  const updatedFieldKeys = Object.keys(updatedFields);
  if (originalFieldKeys.length !== updatedFieldKeys.length) {
    return true;
  }

  return originalFieldKeys.some(componentId => {
    const originalField = originalFields[componentId];
    const updatedField = updatedFields[componentId];
    return isFieldModified(originalField, updatedField);
  });
}

export function isFieldModified(
  originalField: Field | null | undefined,
  updatedField: Field | null | undefined,
  countNullAsModified = false,
): boolean {
  const originalIsEmpty = isNil(originalField) || originalField.isEmpty;
  const updatedIsEmpty = isNil(updatedField) || updatedField.isEmpty;

  // If both are null or empty, they are not dirty
  if (originalIsEmpty && updatedIsEmpty && !countNullAsModified) {
    return false;
  }

  // If one is null or empty, but the other is not, they are dirty.
  // (This requires the above 'both' check to run first.)
  if (originalIsEmpty || updatedIsEmpty) {
    return true;
  }

  // If they are not equal, they are dirty
  return !originalField.isEqual(updatedField);
}

export function getModifiedFields(
  originalFields: FieldRecord | null,
  updatedFields: FieldRecord | null,
  countNullAsModified = false,
): FieldRecord | null {
  if (isNil(originalFields) || isNil(updatedFields)) {
    return null;
  }
  const allFieldIds = uniq([...Object.keys(updatedFields), ...Object.keys(originalFields)]);
  return allFieldIds.reduce((acc, componentId) => {
    const originalField = originalFields[componentId];
    const updatedField = updatedFields[componentId];
    const isModified = isFieldModified(originalField, updatedField, countNullAsModified);
    if (isModified) {
      acc[componentId] = updatedField;
    }
    return acc;
  }, {});
}
